/**
 * Backup controls in the save archive's footer.
 *
 * Two platforms, two honest stories. On Chromium a folder can be connected once
 * and written to for ever after, so the offer is "connect it and forget it". On
 * iOS nothing may be written to disk without a gesture, so the offer is a chip
 * that counts what is not backed up yet and a share sheet that puts the file in
 * Files or iCloud — nicer than a Safari download, and the same one tap.
 *
 * What this deliberately is not: a modal, a blocker, or a prompt on the way
 * out. `pagehide` cannot reliably start a download and iOS kills the page
 * mid-handler; the design says do not build it, so it is not built.
 */

import {
  backupFolderSupported,
  connectBackupFolder,
  connectedFolderName,
  disconnectBackupFolder,
  resumeBackupFolder,
  startAutoBackup,
  writeVaultToFolder,
} from '../backup-folder';
import { lastBackupAt, noteBackupTaken } from '../durability';
import { downloadBlob } from '../saves';
import { buildVaultBackup, markBackedUp, unbackedSaveCount } from '../vault-backup';
import { bytes, confirmDanger, toast } from './dom';

/** Where the chip stops being informational and starts being a colour. */
const NAG_SAVES = 3;
const NAG_DAYS = 7;

export interface BackupControls {
  refresh(): void;
}

/**
 * Hands the file to the platform's best destination.
 *
 * Web Share with a file is the iOS path — the share sheet reaches Files and
 * iCloud Drive, where a Safari download reaches a folder most players never
 * open. Everywhere else, and whenever the share is declined or unsupported, a
 * plain anchor download.
 */
async function deliver(blob: Blob, filename: string): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], filename, { type: 'application/octet-stream' });
  const share = navigator.share?.bind(navigator);
  if (share && navigator.canShare?.({ files: [file] })) {
    try {
      await share({ files: [file], title: 'Rerun Reactor saves' });
      return 'shared';
    } catch (error) {
      // AbortError is the player closing the sheet; that is a decision, not a
      // failure, and re-downloading behind their back would be rude.
      if ((error as DOMException)?.name === 'AbortError') throw error;
    }
  }
  downloadBlob(blob, filename);
  return 'downloaded';
}

export function mountBackupControls(footer: HTMLElement, onChanged: () => void): BackupControls {
  const chip = document.createElement('div');
  chip.id = 'rr-backup-chip';
  chip.hidden = true;

  const chipText = document.createElement('span');
  const chipAction = document.createElement('button');
  chipAction.className = 'tiny primary';
  chipAction.textContent = 'Back up';
  chip.append(chipText, chipAction);

  const row = document.createElement('div');
  row.className = 'row';

  const backupButton = document.createElement('button');
  backupButton.className = 'tiny';
  backupButton.textContent = 'Back up saves';

  const folderButton = document.createElement('button');
  folderButton.className = 'tiny ghost';
  folderButton.hidden = !backupFolderSupported();

  row.append(backupButton, folderButton);
  footer.append(chip, row);

  let busy = false;

  const runBackup = async () => {
    if (busy) return;
    busy = true;
    backupButton.disabled = true;
    chipAction.disabled = true;
    try {
      // A connected folder is the destination the player already chose; asking
      // them to pick a place for a second copy would be a worse answer.
      if (connectedFolderName() && (await writeVaultToFolder())) {
        noteBackupTaken();
        toast(`Backed up to ${connectedFolderName()}.`);
      } else {
        const backup = await buildVaultBackup();
        const how = await deliver(backup.blob, backup.filename);
        await markBackedUp();
        noteBackupTaken();
        toast(
          `${backup.saveCount} save${backup.saveCount === 1 ? '' : 's'} in ${backup.filename} (${bytes(backup.blob.size)})`
            + (how === 'shared' ? ' — sent to the share sheet.' : ' — downloaded.')
        );
      }
      refresh();
      onChanged();
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
      toast(`Backup failed: ${(error as Error).message}`, 'bad');
    } finally {
      busy = false;
      backupButton.disabled = false;
      chipAction.disabled = false;
    }
  };

  backupButton.addEventListener('click', () => void runBackup());
  chipAction.addEventListener('click', () => void runBackup());

  folderButton.addEventListener('click', async () => {
    if (connectedFolderName()) {
      if (!(await confirmDanger(`Stop writing backups to ${connectedFolderName()}? Existing files are left alone.`))) return;
      await disconnectBackupFolder();
      toast('Backup folder disconnected.', 'warn');
      refresh();
      onChanged();
      return;
    }
    try {
      const name = await connectBackupFolder();
      noteBackupTaken();
      toast(`Backing up to ${name} after every save.`);
      refresh();
      onChanged();
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return;
      toast(`Could not connect that folder: ${(error as Error).message}`, 'bad');
    }
  });

  const refresh = () => {
    const folder = connectedFolderName();
    folderButton.hidden = !backupFolderSupported();
    folderButton.textContent = folder ? `Backing up to ${folder} — disconnect` : 'Connect a backup folder…';
    void unbackedSaveCount()
      .then((count) => {
        const last = lastBackupAt();
        const days = last ? (Date.now() - last) / 86_400_000 : Infinity;
        // A nudge with nothing to nudge about is noise. At zero the chip does
        // not render at all — not even emptied-but-present — while the backup
        // buttons and the health line stay put.
        chip.hidden = count === 0;
        chipText.textContent = count === 0
          ? ''
          : count === 1
            ? '1 save not backed up'
            : `${count} saves not backed up`;
        chip.classList.toggle('nag', count > 0 && (count >= NAG_SAVES || days >= NAG_DAYS));
      })
      .catch(() => {
        chip.hidden = true;
      });
  };

  void resumeBackupFolder().then(() => refresh());
  startAutoBackup({
    onWritten: () => {
      noteBackupTaken();
      refresh();
      onChanged();
    },
    onDowngrade: () => {
      // The folder is still remembered; the grant is not. Say so once, then
      // behave exactly like a browser that never had one.
      toast('The backup folder needs permission again — reconnect it from the save archive.', 'warn', 9000);
      refresh();
    },
  });
  refresh();

  return { refresh };
}
